import type {
  WritingCompanionEntitlementResponse,
  WritingCompanionEntitlementStatus,
} from "~/types/WritingCompanionCheckout";
import { retryWritingCompanionActivation } from "~/utils/writing-companion-purchase";

type WritingCompanionEntitlementState = {
  isLoading: boolean;
  hasPurchased: boolean;
  loaded: boolean;
  status?: WritingCompanionEntitlementStatus;
  error?: string;
};

export const useWritingCompanionEntitlementsStore = defineStore(
  "writing-companion-entitlements",
  () => {
    const records = ref<Record<string, WritingCompanionEntitlementState>>({});
    const key = (studentId: string, _classroomId?: string) => studentId;

    const load = async (
      studentId: string,
      classroomId: string,
      force = false
    ) => {
      const recordKey = key(studentId, classroomId);
      const current = records.value[recordKey];
      if (current?.isLoading || (current?.loaded && !force)) return;
      records.value[recordKey] = {
        isLoading: true,
        hasPurchased: current?.hasPurchased ?? false,
        loaded: current?.loaded ?? false,
        status: current?.status,
      };
      try {
        const checkEntitlement = async () => {
          const response = await $fetch<WritingCompanionEntitlementResponse>(
            "/api/companion/purchases/entitlement",
            {
              query: { studentId, classroomId },
              headers: await useApiHeaders(),
            }
          );
          if (response.status === "activationPending") {
            throw Object.assign(new Error("License activation is pending."), {
              response,
            });
          }
          return response;
        };

        let response: WritingCompanionEntitlementResponse;
        try {
          response = await retryWritingCompanionActivation({
            activate: checkEntitlement,
          });
        } catch (error: any) {
          if (!error?.response) throw error;
          response = error.response;
        }
        records.value[recordKey] = {
          isLoading: false,
          hasPurchased: response.hasPurchased,
          loaded: true,
          status: response.status,
        };
      } catch (error: any) {
        records.value[recordKey] = {
          isLoading: false,
          hasPurchased: false,
          loaded: true,
          status: "verificationUnavailable",
          error: error?.data?.message || "Unable to check Writing Companion access.",
        };
      }
    };

    const reset = () => {
      records.value = {};
    };

    return { records, key, load, reset };
  }
);
